/**
 * Composed application providers.
 *
 * Wraps the app with QueryClientProvider (TanStack Query) and any future
 * providers. Auth state is read from Zustand stores, not a React Context
 * provider — so no AuthProvider here.
 *
 * The QueryClient and its global QueryCache/MutationCache error handlers live in
 * `query-client.ts` so tests can build a client with the same surfaces the app
 * runs with (a bare `new QueryClient()` in a test silently drops every error
 * this app relies on being toasted). Queries/mutations that render their own
 * inline error UI opt out with `meta: { suppressToast }`; card-owned handled
 * mutations may also set `suppressErrorLog` so their expected rejection is not
 * reported as unhandled.
 */

import { QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { createAppQueryClient } from './query-client'
import { runBrandDnaCacheVersionCheck } from '@/api/queries/brand-dna'
import { registerProjectPermissionQueryClient } from '@/api/project-mutation-guard'

const queryClient = createAppQueryClient()

registerProjectPermissionQueryClient(queryClient)

// Boot-time cache-bust: if the stored BrandDNA cache version tag differs from
// the current build's tag, wipe the brandDna + agents query trees so we don't
// hand stale, schema-incompatible data to the new code. Runs once at module
// load so it executes before any component mounts.
runBrandDnaCacheVersionCheck(queryClient)

export function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}

/** Exported for direct use in features/notifications/ cache invalidation */
export { queryClient }
